import {
  BTypeMainHead,
  BTypeCardView,
  FUNERAL_SIDO,
  FUNERAL_SIGUNGU,
  ATypeMap,
  CTypeBreadCrumb,
  ATypeNationalMeritSummary,
  FullWidthTwinButton,
  call,
  ATypeNationalMeritDescription,
  GOI_WEB_URL,
  customEvent,
} from "goi_common";
import { GetStaticPaths, GetStaticProps } from "next";

import React from "react";
import { ParlorDetailProps } from "goi_common";
import { parlorRegion } from "@/constants/parlorRegion";
import { Box, Stack } from "@mui/material";
import Head from "next/head";
import facilities from "goi_common/src/constants/facilities_detail.json";

const { regionColor, targetSidos } = parlorRegion;

export const getStaticPaths: GetStaticPaths = async () => {
  const paths = facilities
    .filter((f) => targetSidos.includes(f.sido) && f.is_public)
    .map((f) => ({
      params: {
        companyname: f.companyname,
      },
    }));

  return {
    paths,
    fallback: false,
  };
};

export const getStaticProps: GetStaticProps = async ({ params }) => {
  const code = params?.companyname as string;
  const companyname = code;
  let p_facility;
  let district;
  let name = "";
  let description = "";

  if (companyname) {
    p_facility = facilities.find((f) => f.companyname === companyname);
    if (p_facility) {
      name = p_facility.companyname;

      district = p_facility.address.slice(0, 2);
      if (p_facility.address.split(" ")[0].length == 4)
        district = p_facility.address[0] + p_facility.address[2];

      description = `강원장례정보공개 (고이장례연구소) - ${p_facility.companyname}  | 국가유공자는 장지 준비는 어떻게 해야 할까요? 국가유공자 장례 준비, 강원장례정보공개(고이장례연구소)와 함께 준비하세요.`;
    }
  }

  return {
    props: {
      defaultPFacility: p_facility,
      pageDescription: description,
      pageKeywords: `${p_facility?.companyname}, ${p_facility?.companyname} 국가유공자, ${district} 국가유공자 장례, ${district} 국가유공자 장례 준비, ${district} 국가유공자 장례 비용`,
      pageOgTitle: `${p_facility?.companyname}에서의 국가유공자를 위한 공설 장지 비용 정보를 안내합니다 :: 강원장례정보공개 (고이장례연구소)`,
      pageOgDescription: description,
      pageOgImage: p_facility?.images[0].url || "",
      hasError: !p_facility,
    },
  };
};

export default function companyname({
  defaultPFacility,
  pageOgTitle,
}: {
  defaultPFacility: ParlorDetailProps;
  pageOgTitle: string;
}) {
  const regionPFacilities = facilities.filter((p) =>
    targetSidos.includes(String(p.sido)) && p.is_public
  );
  const sidoName = FUNERAL_SIDO.get(Number(defaultPFacility.sido));
  const sigunguName = FUNERAL_SIGUNGU.get(Number(defaultPFacility.sido))?.get(
    Number(defaultPFacility.sigungu)
  );
  const carouselItems = regionPFacilities.map((res, i) => {
    return {
      "@type": "ListItem",
      name: `(비용안내) ${res.companyname}`,
      image: `${res.images[0].url}`,
      url: `https://www.gangwon-portal.co.kr/gangwon-veterans/facility/${res.companyname}`,
      position: i + 1,
    };
  });

  const carouselRichSearch =
    regionPFacilities.length > 6
      ? {
          "@context": "http://schema.org",
          "@type": "ItemList",
          itemListElement: carouselItems,
        }
      : {};
  return (
    <Stack>
      <Head>
        <title>{pageOgTitle}</title>
        {regionPFacilities.length > 5 && (
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: JSON.stringify(carouselRichSearch),
            }}
          />
        )}
      </Head>
      <CTypeBreadCrumb
        firstItem={{
          text: `국가유공자 장례 추천`,
          href: `${GOI_WEB_URL}?utm_source=dogsound` + "/match/request/?utm_source=dogsound",
          event: {
            domain: "gangwon",
            path: "veterans_facility",
            trigger_and_target: "br_crumb_mth_req",
          },
        }}
        secondItem={{
          text: `국가유공자 장례헬프센터`,
          href: `/gangwon-veterans/`,
          event: {
            domain: "gangwon",
            path: "veterans_facility",
            trigger_and_target: "br_crumb_veterans_facility",
          },
        }}
        goiHomeEvent={{
          domain: "gangwon",
          path: "veterans_facility",
          trigger_and_target: "br_crumb_goi_home",
        }}
      />
      <BTypeMainHead
        sidoName={sidoName}
        sigunguName={sigunguName}
        mainColor={regionColor}
        defaultParlor={defaultPFacility}
        title={`${defaultPFacility.companyname}`}
        managed_type={defaultPFacility.managed_type}
        isJangji
      />

      <Stack
        gap="12px"
        padding="10px 20px 20px"
        margin="20px 12px"
        style={{
          background: "white",
          boxShadow: "0px 0px 12px 0px rgba(0, 0, 0, 0.12)",
        }}
      >
        <ATypeNationalMeritSummary mainColor={regionColor} />
        <Box className="subtitle_14_b" textAlign="center">
          상담을 통해 자세한 국가 유공자 장례 혜택을 안내드립니다.
        </Box>
        <FullWidthTwinButton
          firstBtn={{
            isGoiCall: true,
            text: "상담하기",
            onClick: () => {
              call("1666-9784");
              customEvent({
                domain: "gangwon",
                path: "veterans_facility",
                trigger_and_target: "goi_call",
              });
            },
          }}
          secondBtn={{
            text: "비용 알아보기",
            href: `${GOI_WEB_URL}?utm_source=dogsound` + "/match/request/?utm_source=dogsound",
            onClick: () => {
              customEvent({
                domain: "gangwon",
                path: "veterans_facility",
                trigger_and_target: "uv_mth_req",
              });
            },
          }}
          backgroundColor={regionColor.c300}
        />
      </Stack>

      <Stack
        gap="6px"
        padding="40px 20px 20px"
        margin="20px 12px"
        style={{
          background: "white",
          boxShadow: "0px 0px 12px 0px rgba(0, 0, 0, 0.12)",
        }}
      >
        <ATypeMap defaultParlor={defaultPFacility} />
        <ATypeNationalMeritDescription />
      </Stack>

      <Stack
        gap="6px"
        padding="40px 20px 20px"
        margin="20px 12px"
        style={{
          background: "white",
          boxShadow: "0px 0px 12px 0px rgba(0, 0, 0, 0.12)",
        }}
      >
        <h3 className="subtitle_18_b">강원 국가유공자 장지</h3>
        {regionPFacilities.map((res) => (
          <BTypeCardView
            mainColor={regionColor}
            key={res.companyname}
            name={res.companyname}
            href={`/gangwon-veterans/facility/${res.companyname}/`}
            descriptions={["국가유공자 우선 안치"]}
            imgSrc={res.images[0]?.url ? res.images[0].url : ""}
            event={{
              domain: "gangwon",
              path: "veterans_facility",
              trigger_and_target: "uv_facility_detail",
            }}
          />
        ))}
      </Stack>
    </Stack>
  );
}
